<template>
  <list class="m-0 p-0" v-model="users" :columns="columns" :pagination="pagination"
        :tableRowClassName="tableRowClassName"
        :loading="loading"
        :actions="actions"
        :sort-change="sortChange"
        :default-sort = "{prop: sf, order: so === 'asc' ? 'ascending' : 'descending'}"
        :edit="(props) => $router.push({ name: 'UserEdit', params: { id: props.row.$id } })"
        :remove="(props) => confirmAndDelete(props.row)"
        search-enabled
        :search-query="query"
        @search-change="searchInput"
        :invite="invite"
  >
  </list>
</template>

<script>
  import { mapState, mapActions } from 'vuex'
  import { UserRecord } from 'src/store/models/User'
  import List from '../../Components/BasicList'
  import debounce from 'lodash/debounce'
  import upperFirst from 'lodash/upperFirst'

  export default {
    name: 'UserList',
    components: {List},
    data () {
      return {
        sf: 'username',
        so: 'asc',
        query: '',
        loading: false,
        columns: [
          { name: 'username', text: 'Username', sortable: 'custom', align: 'left', formatter: this.usernameFormatter, minWidth: 150 },
          { name: 'email', text: 'E-mail', sortable: 'custom', align: 'left', minWidth: 300 },
          { name: 'joinedAt', text: 'Join Date', sortable: 'custom', formatter: this.dateFormatter, align: 'left', minWidth: 180 },
          { name: 'lastLogin', text: 'Last Login', sortable: 'custom', formatter: this.dateFormatter, align: 'left', minWidth: 180 },
          { name: 'roles', text: 'Roles', sortable: false, align: 'left', formatter: (row, column, cellValue) => cellValue.map(v => this.$t(upperFirst(v))).join(', ') }
        ],
        actions: [
          {label: 'Add User', click: () => this.checkUserLimit()}
        ],
        debouncedChangeSearch: debounce(this.changeSearch.bind(this), 500),
        invite: { openInvites: 0, click: this.confirmAndSendInvite },
        totalUsers: 0
      }
    },
    methods: {
      ...mapActions('pagination/user', ['paginate']),
      usernameFormatter: function (row, column, cellValue) {
        return row.extern ? `${cellValue} (${this.$t('external')})` : cellValue
      },
      dateFormatter: function (row, column, cellValue) {
        return this.$options.filters.formatDate(cellValue)
      },
      sortChange: function ({ column, prop, order }) {
        const sf = prop || 'createdAt'
        const so = (order || 'descending') === 'ascending' ? 'asc' : 'desc'
        if (sf !== this.sf || so !== this.so) {
          this.sf = sf
          this.so = so
          this.fetchCurrentPage()
        }
      },
      confirmAndDelete: function (user) {
        this.$dialog.confirm(this.$t(`Confirm`), this.$t('Are you sure to delete {user}?', { user: user.email }), this.$t('Delete'))
          .then(async () => {
            await UserRecord.api().delete(`/user/${user._id}`)
            await this.fetchCurrentPage()
          })
      },
      fetchCurrentPage: async function () {
        try {
          const newPage = parseInt(this.$route.query.page, 10) || 1
          this.loading = true
          const result = await UserRecord.api().get('/user', {
            dataKey: 'items',
            params: Object.assign({ search: this.query, sf: this.sf, so: this.so, l: this.pagination.perPage, lo: newPage - 1 }),
            save: false
          })
          if (result.response && result.response.data && result.response.data.total) {
            this.paginate({
              perPage: this.pagination.perPage,
              currentPage: newPage,
              total: result.response.data.total
            })
            this.invite.openInvites = result.response.data.openInvites
            this.totalUsers = result.response.data.total
          }
          await UserRecord.create({data: result.response.data.items})
        }
        catch (e) { console.log('e', e)}
        finally {
          setTimeout( () => this.loading = false, 250)
        }
      },
      changePage (val) {
        const page = val > 1 ? val : undefined
        this.$router.push({ ...this.$router.currentRoute, query: { ...this.$route.query, page } })
      },
      changeSearch () {
        this.$router.push({ ...this.$router.currentRoute, query: { ...this.$route.query, q: this.query, page: 1 } })
      },
      searchInput (val) {
        this.query = val
        this.debouncedChangeSearch.cancel()
        this.debouncedChangeSearch()
      },
      tableRowClassName ({row}) {
        if (row.extern) {
          return 'external-row'
        }
        return ''
      },
      confirmAndSendInvite: function (user) {
        this.$dialog.confirm(this.$t(`Confirm`), this.$t('Are you sure to send invites?'), 'Send')
                .then(async () => {
                  await UserRecord.api().post(`/user/sendInvite`)
                  await this.fetchCurrentPage()
                })
      },
      checkUserLimit() {
        let userLimit = this.$auth.user.customer.settings.contract.userLimit
        if (this.totalUsers >= userLimit){
          this.$dialog.info(this.$t('Information'), this.$t('You reached the user limit.<br>For more informations please contact the administrator.'), 'OK')
        } else {
          return this.$router.push({ name: 'UserCreate' })
        }
      }
    },
    computed: {
      users: () => UserRecord.all(),
      ...mapState('pagination', {
        pagination: (pagination) => pagination.user
      })
    },
    async beforeDestroy () {
      await UserRecord.deleteAll()
    },
    async mounted () {
      this.query = this.$route.query.q
      await this.fetchCurrentPage()
    },
    watch: {
      '$route.query.page' (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.fetchCurrentPage()
        }
      },
      '$route.query.q' (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.fetchCurrentPage()
        }
      }
    }
  }
</script>

<style>
  .external-row>td, .external-row>th {
    background-color: #ecf5f2;
  }

  .external-row>td, .external-row>th:hover {
    background-color: #ecf5f2 !important;
  }
</style>
